import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { FileUpload } from "@/components/FileUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

export default function NewRFQ() {
  const nav = useNavigate();
  const [opts, setOpts] = useState({ processes: [], materials: [] });
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    title: "", process: "", material: "", quantity: 1, tolerance: "", finish: "", deadline: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/meta/options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.process || !form.material) {
      toast.error("Select a process and material");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity), file_ids: files.map((f) => f.file_id) };
      const { data } = await api.post("/rfqs", payload);
      toast.success("RFQ submitted — suppliers notified");
      nav(`/rfqs/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="label-tech text-muted-foreground mb-1">New request for quote</div>
      <h1 className="text-3xl font-bold tracking-tight">Submit an RFQ</h1>
      <p className="mt-2 text-sm text-muted-foreground">Define your part requirements and attach CAD files. You'll get an instant estimate and AI analysis on submit.</p>

      <form onSubmit={submit} className="mt-8 space-y-8">
        <Section title="Part details">
          <FieldWrap label="RFQ title" full>
            <input required data-testid="rfq-title" value={form.title} onChange={set("title")} className="rfq-input" placeholder="e.g. Aluminum bracket — v2 revision" />
          </FieldWrap>
          <FieldWrap label="Process">
            <Select value={form.process} onValueChange={(v) => setForm((f) => ({ ...f, process: v }))}>
              <SelectTrigger data-testid="rfq-process" className="rounded-sm"><SelectValue placeholder="Select process" /></SelectTrigger>
              <SelectContent>
                {opts.processes.map((p) => <SelectItem key={p} value={p} data-testid={`process-opt-${p}`}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldWrap>
          <FieldWrap label="Material">
            <Select value={form.material} onValueChange={(v) => setForm((f) => ({ ...f, material: v }))}>
              <SelectTrigger data-testid="rfq-material" className="rounded-sm"><SelectValue placeholder="Select material" /></SelectTrigger>
              <SelectContent>
                {opts.materials.map((m) => <SelectItem key={m} value={m} data-testid={`material-opt-${m}`}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldWrap>
          <FieldWrap label="Quantity">
            <input required type="number" min="1" data-testid="rfq-quantity" value={form.quantity} onChange={set("quantity")} className="rfq-input" />
          </FieldWrap>
          <FieldWrap label="Target deadline">
            <input type="date" data-testid="rfq-deadline" value={form.deadline} onChange={set("deadline")} className="rfq-input" />
          </FieldWrap>
          <FieldWrap label="Tolerance">
            <input data-testid="rfq-tolerance" value={form.tolerance} onChange={set("tolerance")} className="rfq-input" placeholder="e.g. ±0.05 mm" />
          </FieldWrap>
          <FieldWrap label="Surface finish">
            <input data-testid="rfq-finish" value={form.finish} onChange={set("finish")} className="rfq-input" placeholder="e.g. Anodized, bead-blast" />
          </FieldWrap>
          <FieldWrap label="Notes / requirements" full>
            <textarea rows={4} data-testid="rfq-notes" value={form.notes} onChange={set("notes")} className="rfq-input resize-none" placeholder="Critical features, inspection needs, packaging…" />
          </FieldWrap>
        </Section>

        <Section title="CAD & drawings">
          <div className="col-span-2">
            <FileUpload files={files} setFiles={setFiles} />
          </div>
        </Section>

        <button type="submit" disabled={submitting} data-testid="rfq-submit-button"
          className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submitting ? "Submitting & analyzing…" : "Submit RFQ"}
        </button>
      </form>

      <style>{`.rfq-input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));padding:0.6rem 0.8rem;border-radius:0.25rem;font-size:0.9rem;outline:none;transition:border-color .15s}.rfq-input:focus{border-color:#EA580C}`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-border rounded-sm bg-card">
      <div className="px-6 py-3 border-b border-border label-tech text-muted-foreground">{title}</div>
      <div className="p-6 grid sm:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

function FieldWrap({ label, children, full }) {
  return (
    <label className={full ? "sm:col-span-2 block" : "block"}>
      <span className="label-tech text-muted-foreground block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
